/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { MutableRefObject, useEffect, useState } from 'react';
import styled from 'styled-components';

import { Modal, Button, LoadingButton } from '@common/index';
import { Alert, Divider, Stack, Switch, TextField, ToggleButton, ToggleButtonGroup } from '@mui/material';
import { v4 as uuidv4 } from 'uuid';

import { useDispatch, useSelector } from 'react-redux';
import { flowActions } from '@store/slices';
import { AppDispatch, State, tabActions } from '@store/index';
import { AddOutlined, CodeOutlined, PlayArrowOutlined } from '@mui/icons-material';
import { Translation } from '@components/Flow';
import { useTranslate } from '@common/hooks/useTranslate';
import { useParams } from 'react-router-dom';
import { isNodeError, MessageParameters, MessageParameterType, runWorkflow } from '@store/async/run';
import { InboundMessageNodeState, MediaUrl } from '@components/Flow/Nodes';
import { useQuery } from '@tanstack/react-query';
import { getNumberByMessagingProfile, MessageProfileNumber } from '@requests/telnyx';
import { useSnackbar } from 'notistack';
import { RequestError } from '@requests/common';
import { Label } from '@common/Label';
import { useTheme } from '../../../styles';
import { MediaParameter } from './MediaParameter';
import { useEntityExecution } from '@common/hooks/useEntityExecution';
import { WorkflowExecutionType } from '@common/Node/NodeSidebar/NodeExecutionHeader';
import { MessagingChatMessage } from '@components/Flow/Nodes/Trigger/InboundMessage/MessageClient/message.types';

const Container = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;

  gap: 1.5rem;
  padding-bottom: 0.5rem;

  #type {
    color: ${({ theme }) => theme.text.subtext};
    font-size: 13px;
    margin: 0;
  }
`;

interface Props {
  abortControllerRef: MutableRefObject<AbortController | null>;
  reset: () => void;
  start: () => void;
  pause: () => void;
}

export const WorkflowRunMessage: React.FC<Props> = ({ abortControllerRef, start, pause, reset }) => {
  const dispatch = useDispatch<AppDispatch>();
  const theme = useTheme();

  const { service_id, workflow_id } = useParams();
  const { enqueueSnackbar } = useSnackbar();
  const { translate } = useTranslate();
  const { entity } = useEntityExecution(WorkflowExecutionType.InboundMessage);

  const flow_id = useSelector((state: State) => state.tabs.active.flow_id);
  const savedParameters = useSelector((state: State) => state.tabs.active.run.savedMessagingParameters);
  const commands = useSelector((state: State) => state.tabs.active.commands);
  const messageRun = useSelector((state: State) => state.flow.workflowMessageRun);
  const setOpen = (val: Translation | null) => dispatch(flowActions.setWorkflowMessageRun(val));

  const savedType = savedParameters?.type as 'SMS' | 'MMS';
  const savedSubject = savedParameters?.subject as string;
  const savedText = savedParameters?.text as string;
  const savedMedia = savedParameters?.media_urls as MediaUrl[];

  const [messageType, setMessageType] = useState<'SMS' | 'MMS'>(savedType || 'SMS');
  const [text, setText] = useState<string>(savedText || '');
  const [subject, setSubject] = useState<string>(savedSubject || '');
  const [media, setMedia] = useState<MediaUrl[]>(savedMedia || []);

  const trigger = commands.find((command) => command.config.trigger);
  const triggerState = trigger?.state ? (trigger.state as InboundMessageNodeState) : undefined;
  const messaging_profile_id = triggerState?.messaging_profile_id;

  const {
    data: numbers = [],
    isLoading: isNumbersLoading,
    error: numberError,
  } = useQuery<MessageProfileNumber[], RequestError, MessageProfileNumber[], [string, string | undefined]>({
    queryKey: ['messaging_number', messaging_profile_id],
    queryFn: getNumberByMessagingProfile,
    retry: false,
  });

  const noNumbers = numbers.length === 0 && !isNumbersLoading;
  const disableExecute = Boolean(numberError || !messaging_profile_id || !entity);

  const changeSavedValue = (field: keyof MessageParameters, checked: boolean, value: MessageParameterType) => {
    if (checked && value) {
      dispatch(tabActions.addSavedMessagingParameter({ field, value }));
    } else {
      dispatch(tabActions.deleteSavedMessagingParameter(field));
    }
  };

  const changeType = (newVal: 'SMS' | 'MMS') => {
    setMessageType(newVal);
    dispatch(tabActions.addSavedMessagingParameter({ field: 'type', value: newVal }));
  };

  const resetState = () => {
    setMessageType(savedType || 'SMS');
    setText(savedText || '');
    setSubject(savedSubject || '');
    setMedia(savedMedia || []);
  };

  // update the state when changing tabs
  useEffect(resetState, [flow_id]);

  const closeModal = () => {
    setOpen(null);
  };

  const addMediaUrl = () => {
    setMedia((prev) => prev.concat({ id: uuidv4(), value: '' }));
  };

  const updateMediaUrl = (id: string, value: string) => {
    setMedia((prev) =>
      prev.map((item) => {
        if (item.id === id) {
          return { ...item, value: value };
        }

        return item;
      })
    );
  };

  const deleteMediaUrl = (id: string) => {
    setMedia((prev) => prev.filter((item) => item.id !== id));
  };

  const executeWorkflow = async () => {
    reset();

    const workspace_id = service_id;
    const translation = translate(workflow_id || '');

    start();
    setOpen(null);

    abortControllerRef.current = new AbortController();

    const messageParameters: MessageParameters = {
      type: messageType,
      text,
      subject,
      media_urls: media.map((item) => item.value),
      entity_id: entity?.webhook_id,
    };

    const parameters = { body: null, query: null, queryData: null, messageParameters };

    const message: MessagingChatMessage = {
      id: uuidv4(),
      from: '',
      media_urls: messageType === 'MMS' ? media.map((item) => item.value) : [],
      sent_at: new Date().toISOString(),
      source: 'receiver',
      text,
    };

    dispatch(tabActions.messagingAddMessage(message));

    const response = await dispatch(
      runWorkflow({
        workspace_id,
        workflow_id,
        parameters,
        translation,
        abortController: abortControllerRef.current,
      })
    );

    pause();

    if (isNodeError(response.payload)) {
      enqueueSnackbar(response.payload.error.execution.reason, { variant: 'error', autoHideDuration: 8000 });
      enqueueSnackbar(`Workflow Execution Failed`, { variant: 'error', autoHideDuration: 3000 });
    }
  };

  return (
    <Modal
      open={Boolean(messageRun)}
      background='black'
      setOpen={closeModal}
      header={`Send Inbound Message`}
      info={`To trigger this inbound message workflow, a message needs to be sent to your messaging profile. Enter your message details below to send a message and trigger your workflow. This message is sent from Telnyx and will not incur a charge.`}
      icon={<CodeOutlined />}
      body={
        noNumbers ? (
          <Container>
            <Alert severity='error'>
              This messaging profile does not have any numbers assigned. If this is incorrect, try refreshing your page.
            </Alert>
          </Container>
        ) : (
          <Container>
            <ToggleButtonGroup
              size='small'
              color='primary'
              value={messageType}
              exclusive
              onChange={(_, newVal: 'SMS' | 'MMS') => newVal && changeType(newVal)}
              fullWidth
            >
              <ToggleButton size='small' value='SMS'>
                SMS
              </ToggleButton>
              <ToggleButton size='small' value='MMS'>
                MMS
              </ToggleButton>
            </ToggleButtonGroup>
            <Divider />
            <Stack spacing={1}>
              <Stack display='flex' justifyContent='space-between' direction='row' alignItems='center'>
                <Label title='Message Text' tooltip='This is the content that will be sent in your message.' />
                <Stack direction='row' spacing={0.75} alignItems='center'>
                  <p id='type'>Save Value</p>
                  <Switch
                    size='small'
                    disabled={!text}
                    checked={Boolean(savedParameters?.text)}
                    onChange={(e) => changeSavedValue('text', e.target.checked, text)}
                  />
                </Stack>
              </Stack>
              <TextField
                variant='outlined'
                value={text}
                onChange={(e) => setText(e.target.value)}
                placeholder='Text'
                disabled={Boolean(savedParameters?.text)}
                fullWidth
                minRows={3}
                maxRows={12}
                multiline
              />
            </Stack>
            {messageType === 'MMS' && (
              <Stack spacing={3}>
                <Stack spacing={1}>
                  <Stack display='flex' justifyContent='space-between' direction='row' alignItems='center'>
                    <Label title='Message Subject' tooltip='This is the subject of your MMS message.' />
                    <Stack direction='row' spacing={0.75} alignItems='center'>
                      <p id='type'>Save Value</p>
                      <Switch
                        size='small'
                        disabled={!subject}
                        checked={Boolean(savedParameters?.subject)}
                        onChange={(e) => changeSavedValue('subject', e.target.checked, subject)}
                      />
                    </Stack>
                  </Stack>
                  <TextField
                    variant='outlined'
                    value={subject}
                    disabled={Boolean(savedParameters?.subject)}
                    onChange={(e) => setSubject(e.target.value)}
                    placeholder='Subject'
                    fullWidth
                  />
                </Stack>
                <Stack spacing={1}>
                  <Stack display='flex' justifyContent='space-between' direction='row' alignItems='center'>
                    <Label
                      title='Media URLs'
                      tooltip="This is the list of media URL's that will be sent in the MMS message."
                    />
                    <Stack direction='row' spacing={0.75} alignItems='center'>
                      <p id='type'>Save Value</p>
                      <Switch
                        size='small'
                        disabled={!media.some((item) => item.value)}
                        checked={Boolean(savedParameters?.media_urls)}
                        onChange={(e) => changeSavedValue('media_urls', e.target.checked, media)}
                      />
                    </Stack>
                  </Stack>
                  <Stack spacing={1.5}>
                    {media.map(({ id, value }) => (
                      <MediaParameter
                        key={id}
                        id={id}
                        value={value}
                        disabled={Boolean(savedParameters?.media_urls)}
                        updateMediaUrl={(newVal) => updateMediaUrl(id, newVal)}
                        deleteMediaUrl={deleteMediaUrl}
                      />
                    ))}
                    <Button
                      color='primary'
                      fullWidth
                      size='large'
                      onClick={addMediaUrl}
                      disabled={Boolean(savedParameters?.media_urls)}
                    >
                      <AddOutlined sx={{ fontSize: '24px', color: `${theme.text.standard} !important` }} />
                    </Button>
                  </Stack>
                </Stack>
              </Stack>
            )}
          </Container>
        )
      }
      actions={
        <Stack direction='row' spacing={1}>
          <Button small onClick={closeModal}>
            Close
          </Button>
          <LoadingButton
            onClick={executeWorkflow}
            loading={isNumbersLoading}
            minWidth='100px'
            small
            customColor='primary'
            startIcon={<PlayArrowOutlined />}
            disabled={disableExecute}
          >
            Execute
          </LoadingButton>
        </Stack>
      }
    />
  );
};
